import { type ReactNode, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { getCurrentUser } from "aws-amplify/auth"
import { getCachedAuthSession } from "../utils/userAttributesCache";
import { Loader, View } from "@aws-amplify/ui-react";

interface ProtectedRouteProps {
  children: ReactNode;
}

function ProtectedRoute({ children }: ProtectedRouteProps) {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      await getCurrentUser();

      // Validate the session token is still valid (not just cached user)
      const session = await getCachedAuthSession();
      const idToken = session.tokens?.idToken;

      if (!idToken) {
        // No valid token — session expired, redirect to login without signing out
        // (signOut triggers Hub events that cause redirect loops)
        setIsAuthenticated(false);
        return;
      }

      setIsAuthenticated(true);
    } catch (_error) {
      // Auth check failed — redirect to login without calling signOut
      // Calling signOut here triggers Hub signedOut → App.checkAuth → navigate('/login') loop
      setIsAuthenticated(false);
    }
  };

  if (isAuthenticated === null) {
    return (
      <View padding="2rem" textAlign="center">
        <Loader size="large" />
      </View>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

export default ProtectedRoute;
